import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Check, Phone } from "lucide-react";
import { PageBanner } from "@/components/layout/PageBanner";
import { PlaceholderImage } from "@/components/layout/PlaceholderImage";
import { SlideshowSpot } from "@/components/SlideshowSpot";
import { SafeImg } from "@/components/SafeImg";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { sb } from "@/lib/sb";

const ABOUT_TITLE = "About Homestead Construction | Searcy AR Remodelers";
const ABOUT_DESC =
  "Meet Wesley Rhodes. 16+ years building quality home remodels and custom projects in Searcy, Arkansas with integrity and craftsmanship.";

export const Route = createFileRoute("/about")({
  head: () => ({
    meta: [
      { title: ABOUT_TITLE },
      { name: "description", content: ABOUT_DESC },
      { property: "og:title", content: ABOUT_TITLE },
      { property: "og:description", content: ABOUT_DESC },
      { property: "og:url", content: "https://homesteadconstruction.us/about" },
      { property: "og:type", content: "website" },
      { name: "twitter:title", content: ABOUT_TITLE },
      { name: "twitter:description", content: ABOUT_DESC },
    ],
    links: [{ rel: "canonical", href: "https://homesteadconstruction.us/about" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "AboutPage",
          name: "About Homestead Construction of NEA",
          url: "https://homesteadconstruction.us/about",
          mainEntity: {
            "@type": "Person",
            name: "Wesley Rhodes",
            jobTitle: "Founder",
            worksFor: {
              "@type": "GeneralContractor",
              name: "Homestead Construction of NEA",
            },
            description:
              "Founder of Homestead Construction of NEA with 16+ years in residential construction, trained alongside an Amish crew.",
          },
        }),
      },
    ],
  }),
  component: AboutPage,
});

const checklist = [
  "Building Trust Through Quality Work and Customer Focus",
  "Integrity and Transparency",
  "Efficient, and Timely Service",
];

const values = [
  {
    title: "Customer-Centric Focus",
    body: "Your needs are at the heart of everything we do. We are dedicated to bringing your visions and dreams to life, ensuring your complete satisfaction with every detail.",
  },
  {
    title: "Uncompromising Quality",
    body: "We strive for precision and excellence in every project, from the smallest detail to the grandest design. Our commitment ensures superior craftsmanship and lasting results that stand the test of time.",
  },
  {
    title: "Integrity & Transparency",
    body: "We believe in complete honesty and transparency throughout the entire build process. You'll always receive upfront, clear, and honest communication, ensuring you are informed and confident every step of the way.",
  },
  {
    title: "Positive Outcomes",
    body: "Our open communication, meticulous planning, and proactive problem-solving lead to fewer disappointments and unexpected results. We aim for a smooth, stress-free project experience that delivers precisely what you envisioned.",
  },
  {
    title: "Efficient & Respectful Work",
    body: "We maintain impeccably clean job sites and employ efficient work processes, minimizing disruption to your daily life. Your home is our workspace, and we treat it with the utmost respect.",
  },
  {
    title: "Reliability & Communication",
    body: "We pride ourselves on our reliability. We show up when we say we will, answer our phones promptly, and maintain consistent, clear communication from the initial consultation to project completion. You can always count on us.",
  },
];

const setsApart = [
  "Locally owned and operated in Searcy, Arkansas",
  "Trained alongside an Amish crew — old-world craftsmanship",
  "Licensed (#0450850225) and fully insured",
  "Clean job sites and respectful of your home",
  "Clear, upfront pricing — no surprises",
  "We answer the phone and show up on time",
];

const GALLERY_SLOT_KEYS = Array.from({ length: 12 }, (_, i) => `about.gallery.${i + 1}`);

type GalleryRow = { slot_key: string; webp_url: string | null; original_url: string | null; alt: string | null };

function AboutPage() {
  return (
    <>
      <PageBanner title="About Us" />

      {/* SECTION 1 — Intro */}
      <section className="container mx-auto px-4 py-20">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <p className="eyebrow">About Us</p>
            <h2 className="font-display font-bold uppercase text-3xl md:text-4xl text-ink mt-2 leading-tight">
              Homestead Construction: Building Dreams with Integrity
            </h2>
            <p className="text-ink/70 mt-5 leading-relaxed">
              At Homestead Construction, we are more than just builders; we are craftsmen dedicated to transforming your vision into a tangible reality. Our foundation is built on a legacy of hard work, honesty, and unparalleled quality.
            </p>
            <ul className="mt-6 space-y-3">
              {checklist.map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <Check className="h-5 w-5 text-brand flex-shrink-0 mt-0.5" />
                  <span className="text-ink">{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="relative">
            <div className="aspect-[4/5] w-full overflow-hidden rounded">
              <SlideshowSpot slideshowKey="about.hero" fallbackSlotKeys={["about.hero"]} label="3G5A6850 — About hero" />
            </div>
            <div className="absolute -bottom-6 -left-6 md:-left-10 bg-brand text-white px-6 py-5 rounded shadow-lg max-w-[220px]">
              <div className="font-display font-bold text-3xl leading-none">16</div>
              <div className="font-display font-semibold uppercase text-xs tracking-wide mt-1">
                Years of Experience
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 2 — Story / Values / What Sets Us Apart */}
      <section className="bg-surface-alt py-20">
        <div className="container mx-auto px-4">
          <Tabs defaultValue="story" className="w-full">
            <TabsList className="mx-auto flex h-auto flex-wrap justify-center gap-2 bg-transparent p-0 mb-10">
              <TabsTrigger
                value="story"
                className="font-display font-semibold uppercase tracking-wide px-6 py-3 data-[state=active]:bg-brand data-[state=active]:text-white"
              >
                Our Story
              </TabsTrigger>
              <TabsTrigger
                value="values"
                className="font-display font-semibold uppercase tracking-wide px-6 py-3 data-[state=active]:bg-brand data-[state=active]:text-white"
              >
                Our Key Values
              </TabsTrigger>
              <TabsTrigger
                value="apart"
                className="font-display font-semibold uppercase tracking-wide px-6 py-3 data-[state=active]:bg-brand data-[state=active]:text-white"
              >
                What Sets Us Apart
              </TabsTrigger>
            </TabsList>

            <TabsContent value="story" className="mt-0">
              <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-10 items-center">
                <div className="aspect-[4/3] overflow-hidden rounded">
                  <SlideshowSpot slideshowKey="about.story" fallbackSlotKeys={["about.story"]} label="Our Story" />
                </div>
                <div>
                  <p className="eyebrow">Our Story</p>
                  <h3 className="font-display font-bold uppercase text-2xl md:text-3xl text-ink mt-2">
                    From an Amish crew to Arkansas
                  </h3>
                  <p className="text-ink/70 mt-5 leading-relaxed">
                    Wesley Rhodes has been immersed in the construction industry since 2009, beginning his journey at the age of 16. He honed his skills working alongside an Amish crew for several years, where he was instilled with core values of hard work, honesty, and quality craftsmanship. In 2020, Wesley brought this foundation to Arkansas, establishing Homestead Construction. We genuinely enjoy our work and are passionately committed to making your home improvement dreams a tangible reality.
                  </p>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="values" className="mt-0">
              <div className="grid lg:grid-cols-[1fr_2fr] gap-8 items-start">
                <div className="aspect-[4/5] overflow-hidden rounded">
                  <SlideshowSpot slideshowKey="about.values" fallbackSlotKeys={["about.values"]} label="Our Key Values" />
                </div>
                <div className="grid md:grid-cols-2 gap-6">
                  {values.map((v) => (
                    <div key={v.title} className="bg-white p-6 rounded shadow-sm border-t-4 border-brand">
                      <h3 className="font-display font-bold uppercase text-lg text-ink">{v.title}</h3>
                      <p className="text-ink/70 mt-3 leading-relaxed text-sm">{v.body}</p>
                    </div>
                  ))}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="apart" className="mt-0">
              <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-10 items-center">
                <div className="aspect-[4/3] overflow-hidden rounded">
                  <SlideshowSpot slideshowKey="about.apart" fallbackSlotKeys={["about.apart"]} label="What Sets Us Apart" />
                </div>
                <ul className="space-y-4">
                  {setsApart.map((item) => (
                    <li key={item} className="flex items-start gap-3 bg-white p-4 rounded shadow-sm">
                      <Check className="h-5 w-5 text-brand flex-shrink-0 mt-0.5" />
                      <span className="text-ink">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* SECTION 3 — Gallery */}
      <AboutGallery />

      {/* SECTION 4 — CTA Band */}
      <section className="relative bg-ink text-white">
        <div className="absolute inset-0 bg-black/60" aria-hidden="true" />
        <div className="absolute inset-0 -z-10">
          <SlideshowSpot slideshowKey="about.team" fallbackSlotKeys={["about.team"]} label="3G5A6786 — CTA band" />
        </div>
        <div className="relative container mx-auto px-4 py-24 text-center">
          <p className="eyebrow text-brand-light">Your Vision. Our Craft.</p>
          <p className="font-display text-xl md:text-2xl text-white mt-4 max-w-2xl mx-auto leading-relaxed">
            Transforming houses into dream homes with unparalleled quality and transparent service. Contact us to start your project today!
          </p>
          <div className="mt-8">
            <Button asChild variant="brand" size="xl">
              <a href="tel:5015936361"><Phone className="h-4 w-4" /> Call Us</a>
            </Button>
          </div>
        </div>
      </section>
    </>
  );
}

type CollagePhoto = { url: string; alt: string };

function AboutGallery() {
  const [photos, setPhotos] = useState<CollagePhoto[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [failedUrls, setFailedUrls] = useState<Set<string>>(new Set());
  const markFailed = (url: string) =>
    setFailedUrls((prev) => (prev.has(url) ? prev : new Set(prev).add(url)));


  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Preferred source: the "about.gallery" slideshow (list of media IDs
      // curated from the dashboard slideshow editor).
      const { data: cfgRow } = await sb
        .from("public_app_settings")
        .select("slideshows")
        .eq("id", 1)
        .maybeSingle();
      const cfg = (cfgRow?.slideshows as Record<string, { items?: { mediaId: string; alt?: string | null }[] }> | null)?.["about.gallery"];
      const items = cfg?.items ?? [];

      if (items.length > 0) {
        const ids = items.map((i) => i.mediaId);
        const { resolvePublicMedia } = await import("@/lib/public-media.functions");
        const media = await resolvePublicMedia({ data: { ids } });
        const byId = new Map(media.map((m) => [m.id, m]));
        const built = items
          .map((i, idx) => {
            const m = byId.get(i.mediaId);
            if (!m) return null;
            return {
              url: m.url,
              alt: i.alt || m.alt || `Homestead Construction project photo ${idx + 1} — Searcy, AR`,
            } as CollagePhoto;
          })
          .filter(Boolean) as CollagePhoto[];
        if (!cancelled) {
          setPhotos(built);
          setLoaded(true);
        }
        return;
      }

      // Legacy fallback: render any old per-slot photos so nothing breaks
      // while a user migrates to the new slideshow editor.
      const { data: rows } = await sb
        .from("site_photos")
        .select("slot_key, webp_url, original_url, alt")
        .in("slot_key", GALLERY_SLOT_KEYS);
      const byKey = new Map((rows ?? []).map((r: any) => [r.slot_key, r]));
      const ordered = GALLERY_SLOT_KEYS
        .map((k) => byKey.get(k))
        .filter((r: any) => !!r && !!(r.webp_url || r.original_url))
        .map((r: any, idx: number) => ({
          url: r.webp_url || r.original_url,
          alt: r.alt || `Homestead Construction project photo ${idx + 1} — Searcy, AR`,
        })) as CollagePhoto[];
      if (!cancelled) {
        setPhotos(ordered);
        setLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loaded && photos.length === 0) {
    return (
      <section className="container mx-auto px-4 py-20">
        <div className="text-center mb-10">
          <p className="eyebrow">Our Work</p>
          <h2 className="font-display font-bold uppercase text-3xl md:text-4xl text-ink mt-2">
            Gallery of Work
          </h2>
          <p className="mt-4 text-ink/60 text-sm">
            Add photos in <span className="font-semibold">Site Photos → About → Gallery of Work</span> on the dashboard to populate this gallery.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="container mx-auto px-4 py-20">
      <div className="text-center mb-10">
        <p className="eyebrow">Our Work</p>
        <h2 className="font-display font-bold uppercase text-3xl md:text-4xl text-ink mt-2">
          Gallery of Work
        </h2>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {photos
          .filter((p) => !failedUrls.has(p.url))
          .map((p, i) => (
          <div
            key={p.url}
            className={`overflow-hidden rounded bg-surface-alt ${i % 5 === 0 ? "md:row-span-2 md:aspect-[3/4]" : "aspect-square"}`}
          >
            <SafeImg
              src={p.url}
              alt={p.alt}
              className="w-full h-full object-cover"
              loading="lazy"
              onFail={() => markFailed(p.url)}
            />
          </div>
        ))}
      </div>
    </section>
  );
}


